import * as React from "react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Modal } from "../../../ui/components/Modal";
import { Callout } from "../../../ui/components/Callout";
import { TextInput } from "../../../ui/components/Form";
import * as formStyles from "../../../ui/components/Form/FormInput.style.css";
import { useAuth } from "../../lib/auth/hooks";
import { useOrganizationContextOptional } from "../../lib/organization-context";
import {
  createProduct,
  fetchProductModels,
} from "../../lib/api/products";
import type { ProductModel } from "../../lib/api/products";
import { useI18n } from "../../lib/i18n";
import { useProductTargetOrganizations } from "./useProductTargetOrganizations";

export interface CreateProductModalProps {
  open: boolean;
  setOpen: (v: boolean) => void;
  onCreated?: () => void;
}

interface FormValues {
  name: string;
  productModelId: string;
  isSimulated: boolean;
  assetTag: string;
  serial: string;
  macAddress: string;
  iotThingName: string;
}

const emptyForm = (): FormValues => ({
  name: "",
  productModelId: "",
  isSimulated: false,
  assetTag: "",
  serial: "",
  macAddress: "",
  iotThingName: "",
});

export function CreateProductModal({
  open,
  setOpen,
  onCreated,
}: CreateProductModalProps) {
  const { t, tx } = useI18n();
  const { accessToken } = useAuth();
  const orgContext = useOrganizationContextOptional();
  const token = accessToken ?? localStorage.getItem("access_token");

  const [values, setValues] = React.useState<FormValues>(emptyForm());
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [productModels, setProductModels] = React.useState<ProductModel[]>([]);
  const [modelsLoading, setModelsLoading] = React.useState(false);

  // Load product models when modal opens
  React.useEffect(() => {
    if (!open || !token) return;
    setModelsLoading(true);
    fetchProductModels(token)
      .then(setProductModels)
      .catch(() => setProductModels([]))
      .finally(() => setModelsLoading(false));
  }, [open, token]);

  React.useEffect(() => {
    if (!open) {
      setValues(emptyForm());
      setError(null);
    }
  }, [open]);

  const patch = (field: keyof FormValues, value: string | boolean) => {
    setError(null);
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const { organizations: targetOrgs, loading: orgsLoading } = useProductTargetOrganizations(
    token,
    open,
  );
  const [organizationId, setOrganizationId] = React.useState("");

  React.useEffect(() => {
    if (!open) {
      setOrganizationId("");
      return;
    }
    setOrganizationId((current) => {
      if (current && targetOrgs.some((o) => o.id === current)) return current;
      const preferred = orgContext?.selectedOrgIds?.find((id) =>
        targetOrgs.some((o) => o.id === id),
      );
      return preferred ?? (targetOrgs.length === 1 ? targetOrgs[0]!.id : "");
    });
  }, [open, targetOrgs, orgContext?.selectedOrgIds]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    const name = values.name.trim();
    if (!name) {
      setError("Product name is required.");
      return;
    }
    if (!organizationId) {
      setError("Select the organization that will own this product.");
      return;
    }

    setCreating(true);
    setError(null);
    try {
      await createProduct(
        {
          organizationId,
          name,
          isSimulated: values.isSimulated,
          ...(values.productModelId && { productModelId: values.productModelId }),
          ...(values.assetTag.trim() && { assetTag: values.assetTag.trim() }),
          ...(values.serial.trim() && { serial: values.serial.trim() }),
          ...(values.macAddress.trim() && { macAddress: values.macAddress.trim() }),
          ...(values.iotThingName.trim() && { iotThingName: values.iotThingName.trim() }),
        },
        token,
      );
      setOpen(false);
      onCreated?.();
    } catch (err: any) {
      setError(err?.message || "Failed to create product. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen} minWidth="440px" maxWidth="560px" closeOnClickOff>
      <Modal.Form onSubmit={handleSubmit}>
        <Modal.Header>
          <Text level="h2" weight="bold">
            Create product
          </Text>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label className={formStyles.label}>Owning organization *</label>
              <select
                value={organizationId}
                onChange={(e) => {
                  setError(null);
                  setOrganizationId(e.target.value);
                }}
                disabled={creating || orgsLoading}
                className={formStyles.input}
              >
                <option value="">— Select organization —</option>
                {targetOrgs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
              {!orgsLoading && targetOrgs.length === 0 && (
                <Text level="body-small" color="muted">
                  You don't have permission to create products in any organization.
                </Text>
              )}
            </div>

            {/* Product Model */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label className={formStyles.label}>
                {tx("pro.products.productModel")}
              </label>
              <select
                value={values.productModelId}
                onChange={(e) => patch("productModelId", e.target.value)}
                disabled={creating || modelsLoading}
                className={formStyles.input}
              >
                <option value="">— Select model —</option>
                {productModels.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>

            <TextInput
              label={t("pro.products.productNameRequired")}
              placeholder={t("pro.products.placeholder.productName")}
              value={values.name}
              onChange={(e) => patch("name", e.target.value)}
              autoFocus
              disabled={creating}
            />

            <div style={{ display: "flex", gap: 12 }}>
              <TextInput
                label={t("pro.products.assetTag")}
                placeholder="e.g. LE-A1B2C3"
                value={values.assetTag}
                onChange={(e) => patch("assetTag", e.target.value)}
                disabled={creating}
              />
              <TextInput
                label={t("pro.products.serialNumber")}
                placeholder="e.g. SN-000123"
                value={values.serial}
                onChange={(e) => patch("serial", e.target.value)}
                disabled={creating}
              />
            </div>

            <TextInput
              label={t("pro.products.macAddress")}
              placeholder="e.g. AA:BB:CC:DD:EE:FF"
              value={values.macAddress}
              onChange={(e) => patch("macAddress", e.target.value)}
              disabled={creating}
            />

            <TextInput
              label={t("pro.products.iotThingName")}
              placeholder="e.g. a1b2c3d4-e5f6-..."
              value={values.iotThingName}
              onChange={(e) => patch("iotThingName", e.target.value)}
              disabled={creating}
            />

            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: creating ? "not-allowed" : "pointer" }}>
              <input
                type="checkbox"
                checked={!!values.isSimulated}
                onChange={(e) => patch("isSimulated", e.target.checked)}
                disabled={creating}
                style={{ width: 16, height: 16, cursor: creating ? "not-allowed" : "pointer" }}
              />
              <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <span style={{ fontSize: 14, fontWeight: 500, color: "var(--color-fg)" }}>{tx("pro.products.simulatedProduct")}</span>
                <span style={{ fontSize: 12, color: "var(--color-fgSecondary)" }}>{tx("pro.products.simulatedProductHint")}</span>
              </div>
            </label>
          </div>

          {error && (
            <div style={{ marginTop: 16 }}>
              <Callout variant="error">{error}</Callout>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            color="neutral"
            onClick={() => setOpen(false)}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            disabled={creating || !values.name.trim() || !organizationId}
          >
            {creating ? "Creating…" : "Create product"}
          </Button>
        </Modal.Footer>
      </Modal.Form>
    </Modal>
  );
}
